// Switching circles (33-accounts.md, "Switching", v1.58.48): a switch looks like a start. In the pick's
// own frame the start screen (03) covers the window at once; the reloaded page's first frame is the same
// start screen, its words from sessionStorage `desyr_switching` (the account's user ID, the circle's name
// and colour, and the time of the pick), read once at that start.
import { idColour } from '../core/format';
import { serverOf } from '../core/session';
import { t } from '../i18n';
import { coverForSwitch, startFrom } from '../start/startLayer';

const KEY = 'desyr_switching';
export interface Target {
  id: string;
  /** The circle's name. */
  circle: string;
  /** Its ID colour (1–8), for the circle's tints. */
  colour: number;
  /** The pick (epoch ms): "Opening <Circle>…" 1.5 s after it, the slow words 8 s after it. */
  at?: number;
}

/** The switch under way at this page's start, if any (read once, 33). */
let target: Target | undefined = (() => {
  try {
    const read = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Target | null;
    sessionStorage.removeItem(KEY);
    startFrom(read?.at);
    return read ?? undefined;
  } catch {
    return undefined;
  }
})();

/** The circle being switched to, when it is `userId`'s. */
export const switchingTo = (userId: string | undefined) => (userId && target?.id === userId ? target : undefined);

/** The pick: the start screen drawn in this frame, then `go` (the reload) once it is on screen. */
export function startSwitching(id: string, circle: string, go: () => void): void {
  target = { id, circle, colour: idColour(serverOf(id)), at: Date.now() };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(target));
  } catch {
    // without storage the new page starts as a plain start
  }
  coverForSwitch(t('accounts.opening', { circle }));
  requestAnimationFrame(() => setTimeout(go));
}

/** The account's list is there, or it failed: later starts in this page are plain again. */
export function endSwitching(): void {
  target = undefined;
}

const LOOK = 'desyr_circle_look';
/** The circle's name and colour from its last opening on this device (v1.58.25), for the start's header. */
export function circleLook(userId: string): { name: string; colour: number } | undefined {
  try {
    return (JSON.parse(localStorage.getItem(LOOK) ?? '{}') as Record<string, { name: string; colour: number }>)[userId];
  } catch {
    return undefined;
  }
}

/** A circle opened (or its name arrived): kept for the next start; never a logo. */
export function keepCircleLook(userId: string, name: string): void {
  try {
    const all = JSON.parse(localStorage.getItem(LOOK) ?? '{}') as Record<string, { name: string; colour: number }>;
    if (all[userId]?.name === name) return;
    localStorage.setItem(LOOK, JSON.stringify({ ...all, [userId]: { name, colour: idColour(serverOf(userId)) } }));
  } catch {
    // storage refused: the next start shows the header's shape
  }
}
